/**
 * Single source of truth for the error `code` values the backend can emit.
 *
 * These mirror the BE constant classes (the per-feature `ErrorCodes` files under
 * `EmlakKatilim.Cms.Application` plus the `AppException` base codes). The FE/BE contract is
 * the code value itself, never the message — every code here has a matching
 * Turkish definition in `src/i18n/locales/tr/errors.json`. Reference these
 * constants everywhere instead
 * of writing the raw string (no magic strings); add a code in exactly one place.
 */
export const ErrorCode = {
  // --- AppException base codes ---
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  // MOD-04 — admin login step-1 OK ama 2FA gerekli; error.data.challengeToken taşır.
  AUTH_2FA_REQUIRED: 'AUTH_2FA_REQUIRED',
  CONFLICT: 'CONFLICT',
  // K-5 — dört-göz ilkesi (409): Submit eden kullanıcı kendi içeriğini
  // Approve/Publish edemez (BE WorkflowErrorCodes.SelfApprovalForbidden).
  WORKFLOW_SELF_APPROVAL_FORBIDDEN: 'WORKFLOW_SELF_APPROVAL_FORBIDDEN',
  // Faz 6 F2 — dört-göz açıkken (workflow.requireFourEyes) taslak içerik
  // doğrudan/toplu yayınlanamaz; önce incelemeden geçmeli (BE WorkflowErrorCodes.ReviewRequired, 409).
  WORKFLOW_REVIEW_REQUIRED: 'WORKFLOW_REVIEW_REQUIRED',
  RATE_LIMIT: 'RATE_LIMIT',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
  // REQ-45 / K-C — X-Project-Id üyesi olunmayan bir projeye gönderilirse (403).
  // http.ts global yakalar → güvenli projeye döner + kullanıcı-dostu uyarı.
  PROJECT_ACCESS_DENIED: 'PROJECT_ACCESS_DENIED',

  // --- BusinessRuleException codes (422) ---
  CANNOT_BAN_SELF: 'CANNOT_BAN_SELF',
  CANNOT_DELETE_SELF: 'CANNOT_DELETE_SELF',
  CONCURRENCY: 'CONCURRENCY',
  CYCLE: 'CYCLE',
  DELETED: 'DELETED',
  FILE_REQUIRED: 'FILE_REQUIRED',
  FILES_REQUIRED: 'FILES_REQUIRED',
  HAS_CHILDREN: 'HAS_CHILDREN',
  IMAGE_EMPTY: 'IMAGE_EMPTY',
  IMAGE_NAME_REQUIRED: 'IMAGE_NAME_REQUIRED',
  IMAGE_NOT_IN_VEHICLE: 'IMAGE_NOT_IN_VEHICLE',
  INVALID_ACTION: 'INVALID_ACTION',
  INVALID_CIDR: 'INVALID_CIDR',
  INVALID_FREQUENCY: 'INVALID_FREQUENCY',
  INVALID_LINK_TYPE: 'INVALID_LINK_TYPE',
  INVALID_LOCATION: 'INVALID_LOCATION',
  INVALID_PLACEMENT: 'INVALID_PLACEMENT',
  DUPLICATE_CIDR: 'DUPLICATE_CIDR',
  INVALID_PARENT: 'INVALID_PARENT',
  INVALID_SLUG: 'INVALID_SLUG',
  INVALID_STATE: 'INVALID_STATE',
  INVALID_TARGET: 'INVALID_TARGET',
  KVKK_CONSENT_INVALID: 'KVKK_CONSENT_INVALID',
  KVKK_GRACE_EXPIRED: 'KVKK_GRACE_EXPIRED',
  KVKK_NOT_PENDING: 'KVKK_NOT_PENDING',
  LANGUAGE_DEFAULT_IMMUTABLE: 'LANGUAGE_DEFAULT_IMMUTABLE',
  LANGUAGE_IN_USE: 'LANGUAGE_IN_USE',
  LANGUAGE_INACTIVE: 'LANGUAGE_INACTIVE',
  NO_DEFAULT_LANGUAGE: 'NO_DEFAULT_LANGUAGE',
  LAST_SUPERADMIN: 'LAST_SUPERADMIN',
  LEAD_SOURCE_INVALID: 'LEAD_SOURCE_INVALID',
  LEAD_STATUS_INVALID: 'LEAD_STATUS_INVALID',
  MAIL_ALREADY_SENT: 'MAIL_ALREADY_SENT',
  MENU_CYCLE: 'MENU_CYCLE',
  PARENT_MENU_MISMATCH: 'PARENT_MENU_MISMATCH',
  REDIRECT_EXISTS: 'REDIRECT_EXISTS',
  REDIRECT_LOOP: 'REDIRECT_LOOP',
  REORDER_DUPLICATE_ID: 'REORDER_DUPLICATE_ID',
  REORDER_DUPLICATE_ORDER: 'REORDER_DUPLICATE_ORDER',
  ROLE_HAS_USERS: 'ROLE_HAS_USERS',
  ROLE_SYSTEM: 'ROLE_SYSTEM',
  SLUG_CONFLICT: 'SLUG_CONFLICT',
  SUPERADMIN_LOCKED: 'SUPERADMIN_LOCKED',
  SYSTEM_ROLE_IMMUTABLE: 'SYSTEM_ROLE_IMMUTABLE',
  TAG_NAME_EXISTS: 'TAG_NAME_EXISTS',
  // --- Localization / namespace codes (LocalizationErrorCodes.cs) ---
  // A key already exists for this (namespace, key, languageCode); INVALID_KEY_FORMAT
  // flags a key that violates the segment rule. Namespace rename/delete add two more:
  // NAMESPACE_MERGE_CONFLICT (409 — target namespace already owns a moving key) and
  // NAMESPACE_RENAME_NOOP (400 — new name equals the current one).
  TRANSLATION_KEY_EXISTS: 'TRANSLATION_KEY_EXISTS',
  INVALID_KEY_FORMAT: 'INVALID_KEY_FORMAT',
  NAMESPACE_MERGE_CONFLICT: 'NAMESPACE_MERGE_CONFLICT',
  NAMESPACE_RENAME_NOOP: 'NAMESPACE_RENAME_NOOP',
  USER_ANONYMIZED: 'USER_ANONYMIZED',
  WRONG_MEMBER_TYPE: 'WRONG_MEMBER_TYPE',

  // --- Page builder codes (PageBlockErrorCodes.cs) ---
  // PB_INVALID_BLOCK message is block-scoped ("Block N (tip): field 'x' is
  // required.") and shown verbatim; PB_REF_NOT_FOUND flags a dangling
  // formEmbed/sliderEmbed reference at save time.
  PB_INVALID_BLOCK: 'PB_INVALID_BLOCK',
  PB_REF_NOT_FOUND: 'PB_REF_NOT_FOUND',

  // --- Form module codes (FormErrorCodes.cs) ---
  FORM_INVALID_SLUG: 'FORM_INVALID_SLUG',
  FORM_INVALID_FIELDS: 'FORM_INVALID_FIELDS',
  FORM_FILE_FIELD_NOT_ALLOWED: 'FORM_FILE_FIELD_NOT_ALLOWED',
  FORM_INVALID_EXPORT_FORMAT: 'FORM_INVALID_EXPORT_FORMAT',
  EXPORT_TOO_LARGE: 'EXPORT_TOO_LARGE',

  // --- Forum module codes (ForumErrorCodes.cs) ---
  APPEAL_ALREADY_RESOLVED: 'APPEAL_ALREADY_RESOLVED',
  APPEAL_NOT_FOUND: 'APPEAL_NOT_FOUND',
  BANNED_WORD_CONFLICT: 'BANNED_WORD_CONFLICT',
  BULK_LIMIT_EXCEEDED: 'BULK_LIMIT_EXCEEDED',
  CONTENT_BLOCKED_BY_FILTER: 'CONTENT_BLOCKED_BY_FILTER',
  INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
  MODERATION_ACTION_ALREADY_REVERSED: 'MODERATION_ACTION_ALREADY_REVERSED',
  MODERATION_ACTION_NOT_FOUND: 'MODERATION_ACTION_NOT_FOUND',
  POST_NOT_FOUND: 'POST_NOT_FOUND',
  REPORT_ALREADY_RESOLVED: 'REPORT_ALREADY_RESOLVED',
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  TOPIC_LOCKED: 'TOPIC_LOCKED',
  TOPIC_NOT_FOUND: 'TOPIC_NOT_FOUND',
  USER_BANNED: 'USER_BANNED',

  // --- MOD-18 / LDAP-AD codes (LdapErrorCodes.cs) ---
  // Login dallanması: LDAP kapalıyken (422) local login önerilir; grup eşlenmemişse
  // (403) yetki mesajı; sunucuya ulaşılamıyorsa (422) erişim mesajı. Config/test:
  // TLS zorunlu (422) ve loopback/link-link host reddi (422, SSRF guard).
  LDAP_NOT_ENABLED: 'LDAP_NOT_ENABLED',
  LDAP_NO_ROLE_MAPPING: 'LDAP_NO_ROLE_MAPPING',
  LDAP_UNAVAILABLE: 'LDAP_UNAVAILABLE',
  LDAP_TLS_REQUIRED: 'LDAP_TLS_REQUIRED',
  LDAP_HOST_NOT_ALLOWED: 'LDAP_HOST_NOT_ALLOWED',

  // --- FE-side synthetic codes (normalizeAxiosError) ---
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
